
import { create } from "zustand";
import { devtools } from "zustand/middleware";
import type { AnalyticsResult, ChatMessageData as ApiChatMessage, ChatSessionData, NodeDoneEvent, NodeStartEvent, MessageType, UploadResponse } from "@/services/api";

export type RunPhase = "idle" | "running" | "done" | "error" | "clarification";

export interface NodeState {
  status: "pending" | "active" | "done" | "error";
  elapsed_ms?: number;
  summary?: Record<string, unknown>;
}

export interface ChatMessageData {
  id: string;
  type: MessageType;
  content: string;
  result?: AnalyticsResult | null;
  isLoading?: boolean;
  clarification_questions?: string[];
  timestamp: number;
  sessionId?: string;
}

export interface AgentRun {
  sessionId: string;
  query: string;
  phase: RunPhase;
  nodes: Record<string, NodeState>;
  result: AnalyticsResult | null;
  error: string | null;
  startedAt: number;
  completedAt: number | null;
}

interface AgentStore {
  messages: ChatMessageData[];
  history: AgentRun[];
  current: AgentRun | null;

  dataSource: "db" | "excel";
  uploadedFile: UploadResponse | null;

  /** Warehouse ID sourced from the URL query parameter ?warehouseId=... */
  warehouseId: string | null;

  /** User ID sourced from the URL query parameter ?userId=... */
  userId: string | null;

  /** Text to pre-fill the chat input when a predefined report is selected. */
  prefillQuery: string | null;
  /** ID of the currently selected predefined report (backend resolves the SQL). */
  pendingReportId: string | null;

  /** Persistent chat session ID stored in the config DB. */
  chatSessionId: string | null;
  /** List of saved sessions fetched from the backend. */
  savedSessions: ChatSessionData[];

  // Actions
  addUserMessage: (content: string) => void;
  startRun: (sessionId: string, query: string) => void;
  nodeStarted: (e: NodeStartEvent) => void;
  nodeDone: (e: NodeDoneEvent) => void;
  runComplete: (result: AnalyticsResult) => void;
  runFailed: (error: string) => void;
  runClarification: (questions: string[]) => void;
  selectHistory: (sessionId: string) => void;
  clearCurrent: () => void;
  clearMessages: () => void;
  setDataSource: (source: "db" | "excel") => void;
  setUploadedFile: (file: UploadResponse | null) => void;
  setWarehouseId: (id: string | null) => void;
  setUserId: (id: string | null) => void;
  selectReport: (title: string, reportId: string) => void;
  clearPrefillQuery: () => void;
  clearPendingReport: () => void;
  setChatSessionId: (id: string | null) => void;
  setSavedSessions: (sessions: ChatSessionData[]) => void;
  newChat: () => void;
  loadSessionMessages: (chatSessionId: string, apiMessages: ApiChatMessage[]) => void;
}

const ORDERED_NODES = [
  "supervisor",
  "sql_fetcher",
  "data_analyzer",
  "chart_generator",
  "insight_engine",
];

function freshNodes(): Record<string, NodeState> {
  return Object.fromEntries(ORDERED_NODES.map((n) => [n, { status: "pending" }]));
}

function generateId(): string {
  return Math.random().toString(36).slice(2, 11);
}

export const useAgentStore = create<AgentStore>()(
  devtools(
    (set, get) => ({
      messages: [],
      history: [],
      current: null,
      dataSource: "db",
      uploadedFile: null,
      warehouseId: null,
      userId: null,
      prefillQuery: null,
      pendingReportId: null,
      chatSessionId: null,
      savedSessions: [],

      setDataSource(source) {
        set({ dataSource: source, uploadedFile: null });
      },

      setUploadedFile(file) {
        set({ uploadedFile: file });
      },

      setWarehouseId(id) {
        set({ warehouseId: id });
      },

      setUserId(id) {
        set({ userId: id });
      },

      selectReport(title, reportId) {
        set({ prefillQuery: title, pendingReportId: reportId });
      },

      clearPrefillQuery() {
        set({ prefillQuery: null });
      },

      clearPendingReport() {
        set({ prefillQuery: null, pendingReportId: null });
      },

      addUserMessage(content) {
        const msg: ChatMessageData = {
          id: generateId(),
          type: "user",
          content,
          timestamp: Date.now(),
        };
        set((s) => ({
          messages: [...s.messages, msg],
        }));
      },

      startRun(sessionId, query) {
        const run: AgentRun = {
          sessionId,
          query,
          phase: "running",
          nodes: freshNodes(),
          result: null,
          error: null,
          startedAt: Date.now(),
          completedAt: null,
        };
        
        // Add loading message
        const loadingMsg: ChatMessageData = {
          id: generateId(),
          type: "assistant",
          content: "Analyzing your query…",
          isLoading: true,
          timestamp: Date.now(),
          sessionId,
        };
        
        set((s) => ({
          current: run,
          messages: [...s.messages, loadingMsg],
        }));
      },

      nodeStarted(e) {
        set((s) => {
          if (!s.current) return s;
          return {
            current: {
              ...s.current,
              nodes: {
                ...s.current.nodes,
                [e.node]: { status: "active", elapsed_ms: e.elapsed_ms },
              },
            },
          };
        });
      },

      nodeDone(e) {
        set((s) => {
          if (!s.current) return s;
          const { node, elapsed_ms, ...rest } = e;
          return {
            current: {
              ...s.current,
              nodes: {
                ...s.current.nodes,
                [node]: { status: "done", elapsed_ms, summary: rest },
              },
            },
          };
        });
      },

      runComplete(result) {
        set((s) => {
          if (!s.current) return s;
          const completed: AgentRun = {
            ...s.current,
            phase: "done",
            result,
            completedAt: Date.now(),
          };
          
          // Update the last message with results
          const updatedMessages = s.messages.map((msg, idx) => {
            if (idx === s.messages.length - 1 && msg.type === "assistant") {
              return {
                ...msg,
                content: result.summary || "Analysis complete",
                result,
                isLoading: false,
              };
            }
            return msg;
          });
          
          return {
            current: completed,
            messages: updatedMessages,
            history: [completed, ...s.history].slice(0, 20),
          };
        });
      },

      runFailed(error) {
        set((s) => {
          if (!s.current) return s;

          // Update the last message with error
          const updatedMessages = s.messages.map((msg, idx) => {
            if (idx === s.messages.length - 1 && msg.type === "assistant") {
              return {
                ...msg,
                content: `Error: ${error}`,
                isLoading: false,
              };
            }
            return msg;
          });

          return {
            current: { ...s.current, phase: "error", error, completedAt: Date.now() },
            messages: updatedMessages,
          };
        });
      },

      runClarification(questions) {
        set((s) => {
          if (!s.current) return s;
          const updatedMessages = s.messages.map((msg, idx) => {
            if (idx === s.messages.length - 1 && msg.type === "assistant") {
              return {
                ...msg,
                content: "I need a bit more information to answer that correctly:",
                isLoading: false,
                clarification_questions: questions,
              };
            }
            return msg;
          });
          return {
            current: { ...s.current, phase: "clarification", completedAt: Date.now() },
            messages: updatedMessages,
          };
        });
      },

      selectHistory(sessionId) {
        const run = get().history.find((r) => r.sessionId === sessionId);
        if (run) set({ current: run });
      },

      clearCurrent() {
        set({ current: null });
      },

      clearMessages() {
        set({ messages: [] });
      },

      setChatSessionId(id) {
        set({ chatSessionId: id });
      },

      setSavedSessions(sessions) {
        set({ savedSessions: sessions });
      },

      newChat() {
        set({ messages: [], chatSessionId: null, current: null });
      },

      loadSessionMessages(chatSessionId, apiMessages) {
        const messages: ChatMessageData[] = apiMessages.map((m) => {
          const parsed = m.result_json ? JSON.parse(m.result_json) : undefined;
          const clarificationQs: string[] | undefined =
            parsed?.clarification_questions?.length ? parsed.clarification_questions : undefined;
          return {
            id: m.id,
            type: m.role as MessageType,
            content: m.content || (clarificationQs ? "I need a bit more information to answer that correctly:" : ""),
            result: parsed,
            clarification_questions: clarificationQs,
            timestamp: new Date(m.created_at).getTime(),
            sessionId: m.chat_session_id,
          };
        });
        set({ messages, chatSessionId, current: null });
      },
    }),
    { name: "AgentStore" }
  )
);
